import {
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useParams } from 'react-router-dom'
import {
  fetchAdvisorsThunk,
  cloneAdvisorThunk,
} from '@/redux/asyncThunks/advisor'
import { deleteAdvisorData } from '@/api/configuration'
import {
  deleteAllDocumentsThunk,
  fetchAdvisorDataThunk,
} from '@/redux/asyncThunks/configuration'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import ConfigurationForm from '@/components/settings/ConfigurationForm'
import { ChatbotType } from '@/redux/types/configuration'
import { Advisor } from '@/redux/types/advisor'

import ConfirmationModal from '@/components/OGE/OGEDeletionModal'
import { RootState } from '@/redux/store'
import { GameRoomStatus } from '@/redux/types/room'
import { CreateAdvisorModal } from '@/components/settings/CreateAdvisorModal'
import { TeamsModal } from '@/components/settings/TeamsModal'
import { MCPServerModal } from '@/components/settings/MCPServerModal'
import { ImportModal } from '@/components/Room/ImportModal'
import { UserGroupIcon } from '@heroicons/react/24/solid'
import {
  Cog8ToothIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { useSelector } from 'react-redux'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import { initiateAllAdvisorsExportAPI } from '@/api/room'
import { initiateAdvisorExportAPI } from '@/api/advisor'
import { toast } from 'sonner'
import { Coins, FileDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import { TeamCommunicatorModal } from '@/components/settings/TeamMode/TeamCommunicatorModal'
import { Badge } from '@/components/ui/badge'
import { useLocation, useNavigate } from 'react-router-dom'

export default function AdvisorSettings() {
  const location = useLocation()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'rename'>('create')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showTeamsModal, setShowTeamsModal] = useState(false)
  const [showMCPServerModal, setShowMCPServerModal] = useState(false)
  const [showImportAdvisorsModal, setShowImportAdvisorsModal] = useState<boolean>(false)
  const [showTeamCommunicatorModal, setShowTeamCommunicatorModal] =
    useState(false)
  const [selectedAdvisor, setSelectedAdvisor] = useState('')
  const [showConfig, setShowConfig] = useState(false)
  const [showDeleteAllAdvisorsModal, setShowDeleteAllAdvisorsModal] =
    useState(false)
  const [showDeleteRoomDocsModal, setShowDeleteRoomDocsModal] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const dispatch = useAppDispatch()
  const { advisors, loading } = useAppSelector((state) => state.advisor)
  const { roomId } = useParams()
  const { currentRoom } = useSelector((state: RootState) => state.room)
  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const { openChatBoxes } = useAppSelector(
    (state: RootState) => state.testChatMessages
  )

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomMembers(roomId))
      dispatch(fetchAdvisorsThunk({ roomId }))
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId])

  useEffect(() => {
    if (advisors.length > 0 && !selectedAdvisor) {
      const defaultAdvisor = advisors.find((advisor) => advisor.is_default)
      setSelectedAdvisor(defaultAdvisor?.chatbotid || advisors[0].chatbotid)
    }
  }, [advisors, selectedAdvisor])

  useEffect(() => {
    if (!location.state || typeof location.state !== 'object') return

    const state = location.state as { openTeams?: boolean }
    if (!state.openTeams) return

    setShowTeamsModal(true)
    navigate(location.pathname, { replace: true })
  }, [location.pathname, location.state, navigate])

  const handleCancel = () => {
    setShowConfig(false)
  }

  const handleAdvisorCreated = (advisor: Advisor) => {
    setSelectedAdvisor(advisor.chatbotid)
    setShowConfig(true)
  }

  const handleDeleteAdvisor = async () => {
    try {
      await deleteAdvisorData(selectedAdvisor)
      if (roomId) {
        await dispatch(fetchAdvisorsThunk({ roomId }))
        // Reset selected advisor and hide config
        setSelectedAdvisor('')
        setShowConfig(false)
        setShowDeleteModal(false)
      }
    } catch (error) {
      console.error('Failed to delete advisor', error)
    }
  }

  const handleExportAdvisor = async () => {
    if (!selectedAdvisor) return
    try {
      await initiateAdvisorExportAPI(selectedAdvisor, true)
      toast.success("Advisor export started. You will be notified when it's ready.")
    } catch {
      toast.error('Failed to start advisor export.')
    }
  }

  const handleExportAllAdvisors = async () => {
    if (!roomId) return
    try {
      await initiateAllAdvisorsExportAPI(roomId, true)
      toast.success("Advisor export started. You will be notified when it's ready.")
    } catch {
      toast.error('Failed to start advisor export.')
    }
  }

  // Bulk RAG delete (issue #563): all advisors in the room, or every document in
  // the room (advisors + AI players + world setup).
  const handleBulkDeleteDocuments = async (scope: 'all-advisors' | 'room') => {
    if (!roomId) return
    setIsBulkDeleting(true)
    try {
      const result = await dispatch(
        deleteAllDocumentsThunk({ scope, roomId })
      ).unwrap()
      toast.success(
        `Deleted ${result.documents_deleted} document(s)` +
          (result.documents_unlinked
            ? `, unlinked ${result.documents_unlinked} shared elsewhere`
            : '')
      )
      await dispatch(fetchAdvisorsThunk({ roomId }))
      // Refresh the open advisor's file list if one is being configured.
      if (showConfig && selectedAdvisor) {
        void dispatch(fetchAdvisorDataThunk(selectedAdvisor))
      }
      setShowDeleteAllAdvisorsModal(false)
      setShowDeleteRoomDocsModal(false)
    } catch (error) {
      toast.error(`Failed to delete documents: ${error}`)
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const handleCloneAdvisor = async () => {
    try {
      const result = await dispatch(
        cloneAdvisorThunk({ chatbotid: selectedAdvisor })
      )
      if (cloneAdvisorThunk.fulfilled.match(result)) {
        // Select the newly cloned advisor and show its configuration
        setSelectedAdvisor(result.payload.chatbotid)
        setShowConfig(true)
      }
    } catch (error) {
      console.error('Failed to clone advisor', error)
    }
  }

  const openCreateModal = () => {
    setModalMode('create')
    setShowModal(true)
  }

  const openRenameModal = () => {
    setModalMode('rename')
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
  }

  const openTeamsModal = () => {
    setShowTeamsModal(true)
  }

  const closeTeamsModal = () => {
    setShowTeamsModal(false)
  }

  const openMCPServerModal = () => {
    setShowMCPServerModal(true)
  }

  const closeMCPServerModal = () => {
    setShowMCPServerModal(false)
  }

  const openTeamCommunicatorModal = () => {
    setShowTeamCommunicatorModal(true)
  }

  const closeTeamCommunicatorModal = () => {
    setShowTeamCommunicatorModal(false)
  }

  const isSelectedAdvisorRandr = () => {
    if (!selectedAdvisor) return false
    const advisor = advisors.find((adv) => adv.chatbotid === selectedAdvisor)
    return (
      advisor?.chatbottype === ChatbotType.RANDR || advisor?.is_default === true
    )
  }

  const isSelectedAdvisorAdjudicator = () => {
    if (!selectedAdvisor) return false
    const advisor = advisors.find((adv) => adv.chatbotid === selectedAdvisor)
    return advisor?.chatbottype === ChatbotType.ADJUDICATION
  }

  const deletionAllowed =
    !isSelectedAdvisorRandr() && !isSelectedAdvisorAdjudicator()

  const renamingAllowed =
    !isSelectedAdvisorRandr() && !isSelectedAdvisorAdjudicator()

  const cloningAllowed =
    !isSelectedAdvisorRandr() && !isSelectedAdvisorAdjudicator()

  const getCurrentAdvisor = () => {
    return advisors.find((adv) => adv.chatbotid === selectedAdvisor)
  }

  return (
    <div className='px-24 py-6 space-y-6'>
      <div className='flex gap-x-2 items-center'>
        <h1 className='text-2xl font-bold text-dark-blue'>Advisor Settings</h1>
      </div>

      <p className='text-muted-foreground'>
        Welcome to the Advisor Configuration page. Please use the menu to create
        and navigate through the advisors needed for your game. In order to make
        each advisor effective, system prompts need to be saved and uploaded
        files must be deployed for RAG.
      </p>

      <div className='flex flex-wrap items-center gap-2 sm:gap-3 w-full'>
        <div className='flex flex-wrap items-center gap-2 sm:gap-3 min-w-0'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  onClick={openTeamsModal}
                  className='bg-dark-blue hover:bg-dark-blue-hovered'
                  disabled={openChatBoxes.length !== 0}
                >
                  <UserGroupIcon className='mr-2 w-4 h-4' />
                  Teams
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Manage team assignments and player configurations</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {currentRoom?.game_mode === TimerMode.TEAM_MODE && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type='button'
                    onClick={openTeamCommunicatorModal}
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={openChatBoxes.length !== 0}
                  >
                    <ChatBubbleLeftRightIcon className='mr-2 w-5 h-5' />
                    Team Communicators
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Manage team communicators for each round</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  onClick={openMCPServerModal}
                  className='bg-dark-blue hover:bg-dark-blue-hovered'
                  disabled={openChatBoxes.length !== 0}
                >
                  <Cog8ToothIcon className='mr-2 w-5 h-5' />
                  MCP Servers
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Configure MCP servers for this room</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  onClick={handleExportAllAdvisors}
                  className='bg-dark-blue hover:bg-dark-blue-hovered'
                  disabled={openChatBoxes.length !== 0 || advisors.length === 0}
                >
                  <ArrowUpTrayIcon className='mr-2 w-5 h-5' />
                  Export All Advisors
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Export all advisors with vector embeddings to JSON</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  onClick={() => setShowDeleteAllAdvisorsModal(true)}
                  className='bg-red-600 hover:bg-red-700 text-white'
                  disabled={
                    isBulkDeleting ||
                    isGameCompleted ||
                    openChatBoxes.length !== 0 ||
                    advisors.length === 0
                  }
                >
                  <TrashIcon className='mr-2 w-5 h-5' />
                  Delete All Advisor RAG
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Delete every RAG document for all advisors in this room
                  (embeddings included). Cannot be undone.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  onClick={() => setShowDeleteRoomDocsModal(true)}
                  className='bg-red-700 hover:bg-red-800 text-white'
                  disabled={isBulkDeleting || isGameCompleted || openChatBoxes.length !== 0}
                >
                  <TrashIcon className='mr-2 w-5 h-5' />
                  Delete ALL Room RAG
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Delete every RAG document in this room — all advisors, AI
                  players and world setup. Cannot be undone.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className='flex-shrink-0 ml-auto mr-3 sm:mr-4'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className={cn(
                    'flex items-center gap-2 flex-shrink-0 text-sm sm:text-base border-dark-blue text-dark-blue hover:bg-slate-50',
                    (isGameCompleted || openChatBoxes.length !== 0) &&
                      'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => setShowImportAdvisorsModal(true)}
                  disabled={isGameCompleted || openChatBoxes.length !== 0}
                >
                  <FileDown className='w-3 h-3 sm:w-4 sm:h-4' />
                  Import advisors
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isGameCompleted
                    ? 'Game completed — section import is disabled'
                    : 'Merge advisors from an export JSON file'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className='space-y-4'>
        <label className='block text-sm font-medium'>Select an Advisor</label>
        <div className='flex gap-2 items-center'>
          <Select
            onValueChange={setSelectedAdvisor}
            value={selectedAdvisor}
            disabled={openChatBoxes.length !== 0}
          >
            <SelectTrigger className='w-[300px]'>
              <SelectValue placeholder='None'>
                {selectedAdvisor
                  ? (advisors?.find((a) => a.chatbotid === selectedAdvisor)
                      ?.chatbotname ?? selectedAdvisor)
                  : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {Array.isArray(advisors) &&
                advisors.map((advisor) => {
                  return (
                    <SelectItem
                      key={advisor.chatbotid}
                      value={advisor.chatbotid}
                      className='flex items-center justify-between'
                    >
                      <span className='flex-1'>{advisor.chatbotname}</span>
                      <div className='flex items-center gap-2 ml-2'>
                        {advisor.currency_cost !== undefined &&
                          advisor.currency_cost !== null &&
                          advisor.currency_cost > 0 && (
                            <Badge
                              variant='outline'
                              className='text-xs flex items-center gap-1'
                            >
                              <Coins className='w-3 h-3' />
                              {advisor.currency_cost}
                            </Badge>
                          )}
                        {advisor.player !== null && (
                          <span className='text-xs text-blue-500'>
                            {advisor.player}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  )
                })}
            </SelectContent>
          </Select>

          {renamingAllowed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    onClick={openRenameModal}
                    disabled={
                      !selectedAdvisor ||
                      advisors.length === 0 ||
                      isGameCompleted
                    }
                  >
                    <PencilIcon className='w-5 h-5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {isGameCompleted
                      ? 'Cannot rename advisors after game completion'
                      : !selectedAdvisor
                        ? 'Select an advisor to rename'
                        : 'Rename the selected advisor'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {cloningAllowed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    onClick={handleCloneAdvisor}
                    disabled={
                      !selectedAdvisor ||
                      advisors.length === 0 ||
                      isGameCompleted ||
                      loading
                    }
                  >
                    <DocumentDuplicateIcon className='w-5 h-5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {isGameCompleted
                      ? 'Cannot clone advisors after game completion'
                      : !selectedAdvisor
                        ? 'Select an advisor to clone'
                        : 'Clone the selected advisor with all its configuration and files'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {cloningAllowed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='outline'
                    onClick={handleExportAdvisor}
                    disabled={!selectedAdvisor || advisors.length === 0 || loading}
                  >
                    <ArrowUpTrayIcon className='w-5 h-5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {!selectedAdvisor
                      ? 'Select an advisor to export'
                      : 'Export this advisor to JSON (includes vector embeddings)'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {deletionAllowed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className='bg-red-600 hover:bg-red-700'
                    onClick={() => setShowDeleteModal(true)}
                    disabled={!selectedAdvisor || advisors.length === 0}
                  >
                    <TrashIcon className='w-5 h-5 text-white' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {!selectedAdvisor
                      ? 'Select an advisor to delete'
                      : 'Delete the selected advisor - this action cannot be undone'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className='flex gap-4'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => setShowConfig(true)}
                  disabled={!selectedAdvisor || openChatBoxes.length !== 0}
                  className='bg-dark-blue hover:bg-dark-blue-hovered'
                >
                  <Cog6ToothIcon className='mr-2 w-5 h-5 text-white fill-palladian-green' />
                  Configure
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {!selectedAdvisor
                    ? 'Select an advisor to configure its settings'
                    : "Configure the selected advisor's behavior and settings"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className='bg-dark-blue hover:bg-dark-blue-hovered'
                  onClick={openCreateModal}
                  disabled={isGameCompleted || openChatBoxes.length !== 0}
                >
                  <PlusIcon className='mr-2 w-5 h-5 stroke-palladian-green' />
                  Create New
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isGameCompleted
                    ? 'Cannot create new advisors after game completion'
                    : 'Create a new advisor for the game'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {!showConfig && (
          <div className='flex justify-center w-full'>
            <img
              src='/images/discussion.svg'
              alt='discussion image'
              className='max-w-[200px] mt-20'
            />
          </div>
        )}
      </div>

      {showConfig && selectedAdvisor && (
        <ConfigurationForm
          key={selectedAdvisor}
          onCancel={handleCancel}
          chatbotid={selectedAdvisor}
        />
      )}

      <CreateAdvisorModal
        isOpen={showModal}
        onClose={closeModal}
        onAdvisorCreated={handleAdvisorCreated}
        mode={modalMode}
        existingAdvisor={
          modalMode === 'rename' ? getCurrentAdvisor() : undefined
        }
      />

      {roomId && (
        <>
          <TeamsModal
            isOpen={showTeamsModal}
            onClose={closeTeamsModal}
            roomId={roomId}
          />
          <MCPServerModal
            isOpen={showMCPServerModal}
            onClose={closeMCPServerModal}
            roomId={roomId}
            roomName={currentRoom?.roomname || undefined}
          />
          <TeamCommunicatorModal
            isOpen={showTeamCommunicatorModal}
            onClose={closeTeamCommunicatorModal}
            roomId={roomId}
          />
        </>
      )}

      {roomId && showImportAdvisorsModal && (
        <ImportModal
          key={`${roomId}-advisors-import`}
          isOpen={showImportAdvisorsModal}
          onClose={() => setShowImportAdvisorsModal(false)}
          scope='advisors'
          roomId={roomId}
          roomName={currentRoom?.roomname || undefined}
          onImportComplete={() => {
            setShowImportAdvisorsModal(false)
            void dispatch(fetchAdvisorsThunk({ roomId }))
          }}
        />
      )}

      {deletionAllowed && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAdvisor}
          title='Delete Advisor'
          description='Are you sure you want to delete this Advisor?'
          confirmButtonText={loading ? 'Deleting...' : 'Delete'}
          cancelButtonText='Cancel'
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteAllAdvisorsModal}
        onClose={() => setShowDeleteAllAdvisorsModal(false)}
        onConfirm={() => handleBulkDeleteDocuments('all-advisors')}
        title='Delete all advisor RAG documents?'
        description='This permanently deletes every RAG document for all advisors in this room, including their embeddings. AI player and world-setup documents are not affected. Documents shared elsewhere are unlinked, not destroyed. This cannot be undone.'
        confirmButtonText={isBulkDeleting ? 'Deleting...' : 'Delete All'}
        cancelButtonText='Cancel'
      />

      <ConfirmationModal
        isOpen={showDeleteRoomDocsModal}
        onClose={() => setShowDeleteRoomDocsModal(false)}
        onConfirm={() => handleBulkDeleteDocuments('room')}
        title='Delete ALL RAG documents in this room?'
        description='This permanently deletes every RAG document in the room — all advisors, all AI players, and the world setup — including their embeddings. Documents shared with other rooms are unlinked, not destroyed. This cannot be undone.'
        confirmButtonText={isBulkDeleting ? 'Deleting...' : 'Delete Everything'}
        cancelButtonText='Cancel'
      />
    </div>
  )
}
